/**
 * Editor Store (Zustand)

 * Also owns free-form block CRUD, selection, and clipboard operations
 * (previously in blockStore).
 *
 * Pattern: pull-based queue
 *   - Sidebar calls insertContent() → sets pendingInsert
 *   - Editor reads pendingInsert via selector → processes → calls consumeInsert()
 */

import {create} from 'zustand';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PendingInsert {
  type: string;
  content: any;
  timestamp: number; // ensures each insert is treated as unique
}

export interface Block {
  id: string;
  x?: number;
  y?: number;
  zIndex?: number;
  [key: string]: any;
}

export interface EditorState {
  pendingInsert: PendingInsert | null;
  pendingClear: number | null; // timestamp to detect new clear requests
  // Block state (absorbed from blockStore)
  blocks: Record<string, Block>;
  selectedBlockId: string | null;
  clipboard: Block | null;
}

export interface EditorActions {
  /** Called by Sidebar to queue content for insertion into the editor */
  insertContent: (content: any) => void;
  /** Called by Sidebar to signal "clear all sidebar-inserted content" */
  clearSidebarContent: () => void;
  /** Called by Editor after processing the pending insert */
  consumeInsert: () => void;
  /** Called by Editor after processing the pending clear */
  consumeClear: () => void;
  // Block actions (absorbed from blockStore)
  addBlock: (block: Partial<Block>) => void;
  updateBlock: (id: string, updates: Partial<Block>) => void;
  deleteBlock: (id: string) => void;
  selectBlock: (id: string) => void;
  copyBlock: (id: string) => void;
  pasteBlock: () => void;
  updateZIndex: (id: string, zIndex: number) => void;
  clearSelection: () => void;
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useEditorStore = create<EditorState & EditorActions>()(
  (set, get) => ({
    // Insert queue state
    pendingInsert: null,
    pendingClear: null,

    // Block state
    blocks: {},
    selectedBlockId: null,
    clipboard: null,

    // ─── Insert queue actions ───────────────────────────────────────────────

    insertContent: (content) => {
      set({
        pendingInsert: {
          type: content?.type || 'unknown',
          content,
          timestamp: Date.now()
        }
      });
    },

    clearSidebarContent: () => {
      set({pendingClear: Date.now()});
    },

    consumeInsert: () => {
      set({pendingInsert: null});
    },

    consumeClear: () => {
      set({pendingClear: null});
    },

    // ─── Block actions ──────────────────────────────────────────────────────

    addBlock: (block) => {
      const id = block.id ?? `block-${Date.now()}`;
      const newBlock: Block = {...block, id};
      set((s) => ({
        blocks: {...s.blocks, [id]: newBlock}
      }));
    },

    updateBlock: (id, updates) => {
      set((s) => {
        if (!s.blocks[id]) return s;

        return {
          blocks: {
            ...s.blocks,
            [id]: {...s.blocks[id], ...updates}
          }
        };
      });
    },

    deleteBlock: (id) => {
      set((s) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {[id]: _, ...remainingBlocks} = s.blocks;

        return {
          blocks: remainingBlocks,
          selectedBlockId: s.selectedBlockId === id ? null : s.selectedBlockId
        };
      });
    },

    selectBlock: (id) => {
      set({selectedBlockId: id});
    },

    copyBlock: (id) => {
      const block = get().blocks[id];
      if (block) set({clipboard: {...block}});
    },

    pasteBlock: () => {
      const {clipboard} = get();
      if (!clipboard) return;
      const pastedBlock: Block = {
        ...clipboard,
        id: `block-${Date.now()}`,
        x: (clipboard.x ?? 0) + 20,
        y: (clipboard.y ?? 0) + 20
      };
      set((s) => ({
        blocks: {...s.blocks, [pastedBlock.id]: pastedBlock}
      }));
    },

    updateZIndex: (id, zIndex) => {
      set((s) => {
        if (!s.blocks[id]) return s;

        return {
          blocks: {
            ...s.blocks,
            [id]: {...s.blocks[id], zIndex}
          }
        };
      });
    },

    clearSelection: () => {
      set({selectedBlockId: null});
    }
  })
);
